import {
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'underscore';
import {
  catchError,
  distinctUntilChanged,
  first,
  last,
  map,
  mergeMap,
  take,
  takeUntil
} from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';

import { EmergencyContact } from '../../../../models/emergency-contact';
import { BaseComponent } from '../../../../components/base/base.component';
import { AdminService } from '../../../../services/admin.service';
import { PeopleService } from '../../../../services/people.service';
import { EmploymentTypes } from '../../../../models/employment-types';
import { SharedService } from '../../../../services/shared.service';
import { CompanyEarningConfig } from '../../../../models/company-earning-config';
import { ModalStates } from '../../../../models/modal';
import { getObjectDeltas } from '../../../../utils/object-deltas';
import { MustMatch } from '../../../../utils/must-match.validator';
import { AuthService } from '../../../../services/auth.service';
import { ModalService } from '../../../../services/modal.service';
import { Client } from '../../../../models/client';
import { AuditService } from '../../../../services/audit.service';
import { Person } from '../../../../models/person';
import { InstanceTypes } from '../../../../models/instance-types';
import {
  AddressChangeRequest,
  ChangeRequestData,
  ChangeRequestHistoryEntry
} from '../../../../models/change-requests';
import { ChangeRequestService } from '../../../../services/change-request.service';
import { Select, Selector, Store } from '@ngxs/store';
import {
  ClientSelectionLoadOpenChangeRequests,
  ClientSelectionState
} from '../../../../state/ClientSelectionState/ClientSelection.state';
import { EmployeeStatus } from '../../../../models/person-status';
import { ToastService } from '../../../../services/toast.service';
import { Enum } from '../../../../models/base';
import { EnumService } from '../../../../services/enum.service';
import { EmailDomainBlacklistValidator } from '../../../../utils/emailDomainBlacklistValidator';
import {
  JobDetailsFormSelectors,
  PersonalDetailsFormSelectors,
  PersonEditorState
} from '../../../../state/PersonEditorState/PersonEditor.state';
import {
  AddEmergencyContact,
  ClearEmploymentTypeCollections,
  ClearSSN,
  EditEmergencyContact,
  LoadPerson,
  LoadPersonalDetailsForm,
  RemoveEmergencyContact,
  ResetAddressFields,
  SetCurrentTabDirty,
  SetCurrentTabStatus,
  SetFederalIdChanged,
  SetHeader,
  SetMaskFederalId,
  SetPersonHasFederalId,
  SetShowVerifyFederalId
} from '../../../../state/PersonEditorState/PersonEditor.actions';
import { SetLaborAllocationTotal } from '../../../../state/PersonEditorState/PersonEditor.JobDetailsForm.actions';
import {
  SetBirthday,
  SetCity,
  SetCountry,
  SetEmploymentTypeId,
  SetFederalIdentifier,
  SetFirstName,
  SetGender,
  SetHomePhoneNumber,
  SetIsCompany,
  SetIsNonUSAddress,
  SetLastName,
  SetMaritalStatus,
  SetMiddleName,
  SetMobilePhoneNumber,
  SetPersonalDetailsFormState,
  SetPersonalEmailAddress,
  SetPostalCode,
  SetRace,
  SetState,
  SetStreetAddress,
  SetStreetAddress2,
  SetUseEIN,
  SetVerifyFederalIdentifier
} from '../../../../state/PersonEditorState/PersonEditor.PersonalDetailsForm.actions';
import { AclService } from '../../../../services/acl.service';

@Component({
  selector: 'app-personal-details-tab',
  templateUrl: './personal-details-tab.component.html',
  styleUrls: ['./personal-details-tab.component.css'],
  standalone: false
})
export class PersonalDetailsTabComponent extends BaseComponent implements OnInit, OnDestroy {
  @Select(ClientSelectionState.client)
  client: Observable<Client>;

  @Select(PersonEditorState.getReadOnly) readOnly: Observable<boolean>;

  personalDeetsForm: UntypedFormGroup;
  @Select(PersonEditorState.getPerson)
  person: Observable<Person>;

  @Select(PersonEditorState.getEmergencyContacts) emergencyContacts: Observable<EmergencyContact[]>;

  @Select(PersonEditorState.getMaskFederalId) maskFederalId: Observable<boolean>;

  @Select(PersonEditorState.hasFederalIdentifier)
  personHasFederalId: Observable<boolean>;

  hasActiveAddressChangeRequest: boolean;
  activeAddressChangeRequestData: ChangeRequestData<AddressChangeRequest>;
  activeAddressChangeRequest: ChangeRequestHistoryEntry | null = null;
  canSubmitRequests: boolean = false;

  private destroy$ = new Subject<void>();

  private initFinished = false;

  get isEmployeePortal() {
    return this.env.instanceType === InstanceTypes.EmployeePortal;
  }
  get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }

  get addressChanged() {
    return (
      this.personalDeetsForm.get('StreetAddress').dirty ||
      this.personalDeetsForm.get('StreetAddress2').dirty ||
      this.personalDeetsForm.get('City').dirty ||
      this.personalDeetsForm.get('State').dirty ||
      this.personalDeetsForm.get('PostalCode').dirty ||
      this.personalDeetsForm.get('Country').dirty ||
      this.personalDeetsForm.get('IsNonUSAddress').dirty
    );
  }

  get addressValid() {
    return (
      this.personalDeetsForm.get('StreetAddress').valid &&
      this.personalDeetsForm.get('StreetAddress2').valid &&
      this.personalDeetsForm.get('City').valid &&
      this.personalDeetsForm.get('State').valid &&
      this.personalDeetsForm.get('PostalCode').valid &&
      this.personalDeetsForm.get('Country').valid
    );
  }

  // enums
  genders = [new Enum('M', 'Male'), new Enum('F', 'Female')];
  maritalStatuses: Enum[] = [];
  races = [];

  @Select(PersonEditorState.getStates)
  states: Observable<Enum[]>;
  @Select(PersonEditorState.getFederalLastFour)
  getFedIdLastFour: Observable<string>;

  canMarkPrimaryEmergencyContact: boolean;
  clientCode: string;
  disable1099: boolean;
  disableK1: boolean;
  employmentTypes = EmploymentTypes;
  emergencyContactForm: UntypedFormGroup;
  maskVerifyFederalId = false;
  selEmploymentType: string;
  showFederalIdVerification = false;
  today = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  };
  zipCodeValidLength = 5; // 5 or 9

  private readonly toastService = inject(ToastService);

  constructor(
    private adminService: AdminService,
    private auditService: AuditService,
    public authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    private peopleService: PeopleService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private changeRequestService: ChangeRequestService,
    @Inject('env') private env: any,
    private store: Store,
    private enumService: EnumService,
    private emailBlacklistValidator: EmailDomainBlacklistValidator,
    private aclService: AclService
  ) {
    super(); // call to parent constructor
  }

  get isPersonActive(): Observable<boolean> {
    return this.store.selectOnce(PersonEditorState.getEmployeeStatuses).pipe(
      map(statuses => {
        const sorted = structuredClone(statuses).sort((a: EmployeeStatus, b: EmployeeStatus) => {
          return new Date(b.DateStatusChange).getTime() - new Date(a.DateStatusChange).getTime();
        });
        if (sorted.length < 1) return false;
        return sorted[0].StatusDescription == 'Active';
      })
    );
  }
  get e() {
    return this.emergencyContactForm.controls;
  }
  get f() {
    return this.personalDeetsForm.controls;
  }

  get canEditEmergencyContacts() {
    return (
      this.authService.hasPermission('employee_emergency_contacts_phone_number', 'W') ||
      this.authService.hasPermission('employee_emergency_contacts_primary_contact', 'W') ||
      this.authService.hasPermission('employee_emergency_contacts_relationship', 'W') ||
      this.authService.hasPermission('employee_emergency_contacts_full_name', 'W')
    );
  }

  addEmergencyContact(content) {
    this.setPrimaryContactFlag();
    this.store.selectOnce(PersonEditorState.getEmergencyContacts).subscribe(emergencyContacts => {
      if (emergencyContacts.length === 0) {
        this.e.IsPrimary.setValue(true);
      }

      this.ngModalService
        .open(content, {
          backdrop: 'static',
          keyboard: false,
          windowClass: 'centered'
        })
        .result.then(
          () => {
            // success
            if (this.emergencyContactForm.valid) {
              // push saved data from form to model
              const contact = new EmergencyContact();
              _.extend(contact, this.emergencyContactForm.value, {
                IsPrimary: this.e.IsPrimary.value ? true : false
              });

              this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
                if (p.Id) {
                  this.peopleService
                    .createEmergencyContact(this.clientCode, p.Id, contact)
                    .subscribe(response => {
                      this.store.dispatch(new AddEmergencyContact(response.Model)).subscribe(() => {
                        this.toastService.show('Save successful');

                        this.sharedService.personEditsSaved.next();

                        // record changes in the audit db
                        const deltas = getObjectDeltas(null, response.Model, ['IntegrityHash']);
                        this.auditService.auditEmployee(
                          `employee-emergency-contact-created`,
                          {
                            ClientCode: this.clientCode,
                            PersonId: p.Id,
                            EmployeeNumber: p.EmployeeId,
                            EmployeeName: `${p.LastName}, ${p.FirstName}`
                          },
                          deltas
                        );
                      });
                    });
                } else {
                  this.store.dispatch(new AddEmergencyContact(contact));
                }
              });
            }
            this.emergencyContactForm.reset();
          },
          () => {
            // dismiss
            this.emergencyContactForm.reset();
          }
        );
    });
  }

  deleteEmergencyContact(contact: EmergencyContact) {
    this.modalService.showConfirmModal(
      'Are you sure you would like to delete this emergency contact?'
    );
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (confirm) {
        this.store.dispatch(new RemoveEmergencyContact(contact)).subscribe(() => {
          this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
            if (p.Id) {
              this.peopleService
                .deleteEmergencyContact(this.clientCode, p.Id, contact.Id)
                .subscribe(() => {
                  this.toastService.show('Delete Successful');
                  this.sharedService.personEditsSaved.next();

                  // record changes in the audit db
                  const deltas = getObjectDeltas(contact, null, ['IntegrityHash']);
                  this.auditService.auditEmployee(
                    `employee-emergency-contact-deleted`,
                    {
                      ClientCode: this.clientCode,
                      PersonId: p.Id,
                      EmployeeNumber: p.EmployeeId,
                      EmployeeName: `${p.LastName}, ${p.FirstName}`
                    },
                    deltas
                  );
                });
            }
          });
        });
      }
    });
  }

  editEmergencyContact(contact: EmergencyContact, content) {
    this.setPrimaryContactFlag(contact);

    // load values into form
    const clone = new EmergencyContact();
    _.extend(clone, contact);
    this.emergencyContactForm.patchValue(clone);

    this.ngModalService
      .open(content, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'centered'
      })
      .result.then(
        result => {
          // success
          if (this.emergencyContactForm.valid) {
            // extract values from form
            const c = new EmergencyContact();
            _.extend(c, this.emergencyContactForm.value, {
              Id: contact.Id,
              IsPrimary: this.e.IsPrimary.value ? true : false
            });

            this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
              if (p.Id) {
                this.peopleService
                  .updateEmergencyContact(this.clientCode, p.Id, c)
                  .subscribe(response => {
                    this.store
                      .selectOnce(PersonEditorState.getEmergencyContacts)
                      .subscribe(emergencyContacts => {
                        const index = emergencyContacts.indexOf(contact);
                        const _contact = { ...emergencyContacts[index] };
                        this.store
                          .dispatch(new EditEmergencyContact(response.Model))
                          .subscribe(() => {
                            this.toastService.show('Save successful');

                            this.sharedService.personEditsSaved.next();

                            // record changes in the audit db
                            const deltas = getObjectDeltas(
                              _contact,
                              this.emergencyContacts[index],
                              ['IntegrityHash']
                            );
                            if (Object.keys(deltas).length > 0) {
                              this.auditService.auditEmployee(
                                `employee-emergency-contact-updated`,
                                {
                                  ClientCode: this.clientCode,
                                  PersonId: p.Id,
                                  EmployeeNumber: p.EmployeeId,
                                  EmployeeName: `${p.LastName}, ${p.FirstName}`
                                },
                                deltas
                              );
                            }
                          });
                      });
                  });
              } else {
                this.store.dispatch(new EditEmergencyContact(c));
              }
            });
          }
          this.emergencyContactForm.reset();
        },
        () => {
          // dismiss
          this.emergencyContactForm.reset();
        }
      );
  }

  clearFederalId() {
    this._setFederalIdValidation(true, true);
  }

  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
        if (this.clientCode != null && p?.Id != null) {
          if(this.isClientPortal) {
            //need to do a permissions check
            this.aclService.CurrentUserAccessCheck(
              'Client Portal Admin',
              'addressChange',
              this.clientCode).subscribe((result) => {
              if(result) {
                this._initChangeRequests();
              }
            });
          } else {
            this._initChangeRequests();
          }
        }
        this._initForm();
        this._loadEnums().subscribe(() => {
          this.emergencyContactForm = this.formBuilder.group({
            IsPrimary: new FormControl(),
            FullName: new FormControl('', [Validators.required]),
            Relationship: new FormControl(),
            PhoneNumber: new FormControl('', [Validators.required])
          });

          this.setZipCodeValidation();

          this.selEmploymentType = this.f.EmploymentTypeId.value;
          this.store.selectOnce(PersonEditorState.getEditorMode).subscribe(mode => {
            if (mode == 'add' && this.initFinished) {
              this.f.EmploymentTypeId.valueChanges.pipe(distinctUntilChanged()).subscribe(val => {
                // clear the FederalId if the employment type changes during person add
                // in order to properly revalidate the fed Id and eliminate issue
                // where user can skip the 'verify' entry field
                this._setFederalIdValidation(true, true);

                // if the user had selected EIN federal id
                // then switches to W2 or K1 - we need to change it to SSN
                if (
                  (val == EmploymentTypes.W2 || val == EmploymentTypes.K1) &&
                  this.f.UseEIN.value === '1'
                ) {
                  this.f.UseEIN.setValue('0');
                }

                if (
                  (val == EmploymentTypes.W2 || val == EmploymentTypes.K1) &&
                  this.f.IsCompany.value
                ) {
                  this.f.IsCompany.setValue(false);
                }
              });
            }
          });

          if (this.f.EmploymentTypeId.value == EmploymentTypes.K1 && this.f.IsCompany.value) {
            this.f.IsCompany.setValue(false);
          }

          if (!this.f.UseEIN.value && this.f.UseEIN.value !== 0) {
            this.f.UseEIN.setValue('0');
          }

          this.store
            .select(PersonEditorState.getShowVerifyFederalId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(show => {
              this.showFederalIdVerification = show;
            });

          this._setAddressValidation(false);

          this.f.IsNonUSAddress.valueChanges.subscribe(() => {
            if (this.f.IsNonUSAddress.dirty) {
              this._setAddressValidation(true);
            }
          });

          this._setNameValidation();
          this.f.IsCompany.valueChanges.subscribe(() => this._setNameValidation());

          this._setFederalIdValidation(false, !p.FederalIdentifier);

          // if creating new person -make 1099-MISC selection available if this client has any active 1099 earnings
          // else, if employment type is already 1099 and client does NOT have 1099 earnings - alert user
          // same rules for K1

          this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
            const m = earningConfigs.find(x => x.EmploymentTypeId === EmploymentTypes.M1099);
            const k = earningConfigs.find(x => x.EmploymentTypeId === EmploymentTypes.K1);
            if (!p.Id) {
              this.disable1099 = !m;
              this.disableK1 = !k;
            } else if (
              !m &&
              p.Id &&
              this.personalDeetsForm.controls.EmploymentTypeId.value == EmploymentTypes.M1099
            ) {
              this._showActiveEarningWarning(this.f.EmploymentTypeId.value);
            } else if (
              !k &&
              p.Id &&
              this.personalDeetsForm.controls.EmploymentTypeId.value == EmploymentTypes.K1
            ) {
              this._showActiveEarningWarning(this.f.EmploymentTypeId.value);
            }
          });

          // when adding a new person
          // if user navigates away and then back to person details
          // before user has been saved - we need to mask the federal id
          if (this.f.FederalIdentifier.value) {
            this.store.dispatch(new SetMaskFederalId(true));
          }

          this.store.selectOnce(PersonEditorState.getReadOnly).subscribe(readOnly => {
            if (readOnly) {
              this.disableAllFormControls(this.emergencyContactForm);
            }
          });
          this.initFinished = true;
        });
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  disableAddressControls() {
    //disable the fields
    this.f.StreetAddress.disable();
    this.f.StreetAddress2.disable();
    this.f.City.disable();
    this.f.State.disable();
    this.f.PostalCode.disable();
    this.f.Country.disable();
    this.f.IsNonUSAddress.disable();
  }
  enableAddressControls() {
    //disable the fields
    this.f.StreetAddress.enable();
    this.f.StreetAddress2.enable();
    this.f.City.enable();
    this.f.State.enable();
    this.f.PostalCode.enable();
    this.f.Country.enable();
    this.f.IsNonUSAddress.enable();
  }

  federalIdBlur() {
    const ssn = this.personalDeetsForm.get('FederalIdentifier');
    if (ssn.value && ssn.value.length === 9) {
      this.store.dispatch(new SetMaskFederalId(true));
    }
  }

  federalIdChange() {
    // this.federalIdChanged to detect if user changed input, and not set from server
    // ngModelChange fires this to display verify ssn field immediately as valid ssn is entered
    // and users can tab into the verify ssn field next
    this.store.selectOnce(PersonEditorState.getFederalIdChanged).subscribe(fedIdChanged => {
      if (fedIdChanged) {
        const ssn = this.personalDeetsForm.get('FederalIdentifier');
        const vssn = this.personalDeetsForm.get('VerifyFederalIdentifier');

        if (ssn.value) {
          if (ssn.valid) {
            // there is a display bug with the ngx-mask library used to hide this input
            // we need to briefly flip values on [hiddenInput] when clearing the value
            this.maskVerifyFederalId = false;
            setTimeout(() => {
              this.maskVerifyFederalId = true;
            }, 100);
          }
          vssn.setValue('');
          this.store.dispatch(new SetShowVerifyFederalId(ssn.valid));
          this.showFederalIdVerification = ssn.valid;
          vssn.setValidators([Validators.required, Validators.minLength(9)]);
          this.personalDeetsForm.setValidators([
            MustMatch('FederalIdentifier', 'VerifyFederalIdentifier').bind(this)
          ]);
        } else {
          vssn.setValue('');
          vssn.setValidators(null);
          this.personalDeetsForm.setValidators(null);
          this.showFederalIdVerification = this.maskVerifyFederalId = false;
          this.store.dispatch(new SetMaskFederalId(false));
          this.store.dispatch(new SetShowVerifyFederalId(false));
        }

        vssn.updateValueAndValidity();
        this.personalDeetsForm.updateValueAndValidity();
      }
    });
  }

  onEmploymentTypeChange() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
      this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
        // check to see if this newly selected employment type has any active earnings. if not - warn user
        const hasActiveEarning = earningConfigs.find(
          x => x.EmploymentTypeId == this.f.EmploymentTypeId.value && x.IsActive
        );

        // if we are creating a new person and user changes employment type before person record has saved
        // we need to clear employment type specific data from this person
        this.store.selectOnce(PersonEditorState.getHasChangedTabs).subscribe(hasChangedTabs => {
          if (!p.Id && hasChangedTabs) {
            const msg =
              'Changing the employment type will clear workers compensation, taxes, earning and deduction information. Do you want to continue?';
            this.modalService.showConfirmModal(
              msg,
              'Do not change Employment Type',
              'Change Employment type'
            );
            this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
              if (confirm) {
                this.selEmploymentType = this.f.EmploymentTypeId.value;
                // emit function to parent to clear earnings/deductions/taxes/workers comp
                this.store.dispatch(new ClearEmploymentTypeCollections());

                // show warning if the newly selected employment type does not have any active earnings on this client
                if (!hasActiveEarning) {
                  this._showActiveEarningWarning(this.f.EmploymentTypeId.value);
                }
              } else {
                // revert employment type back
                this.f.EmploymentTypeId.setValue(this.selEmploymentType);
              }
            });
          } else if (!hasActiveEarning) {
            this._showActiveEarningWarning(this.f.EmploymentTypeId.value);
          }
        });
      });
    });
  }

  onFederalIdChange() {
    if (this.initFinished) {
      return this.store.dispatch(new SetFederalIdChanged(true));
    }
    return of();
  }

  onUseEINChange() {
    this._setFederalIdValidation(true, true);
  }

  setPrimaryContactFlag(contact: EmergencyContact = null) {
    this.store.selectOnce(PersonEditorState.getEmergencyContacts).subscribe(emergencyContacts => {
      // if there is already another emergency contact marked as primary
      // hide Primary Contact checkbox
      const primary = emergencyContacts.find(x => x.IsPrimary);
      this.canMarkPrimaryEmergencyContact = !(primary && (contact === null || !contact.IsPrimary));
    });
  }

  setZipCodeValidation() {
    // set 5/9 validation if 'non US addr' checkbox is unchecked
    const isNonUs = this.personalDeetsForm.get('IsNonUSAddress');
    if (!isNonUs.value) {
      // allow only 5 or 9 digits
      const zipCtrl = this.personalDeetsForm.get('PostalCode');
      if (!zipCtrl.value) {
        zipCtrl.setValidators([Validators.minLength(5), Validators.required]);
      } else if (zipCtrl.value.length <= 5 && this.zipCodeValidLength === 9) {
        zipCtrl.setValidators([Validators.minLength(5), Validators.required]);
        this.zipCodeValidLength = 5;
        zipCtrl.updateValueAndValidity();
      } else if (zipCtrl.value.length > 5 && this.zipCodeValidLength === 5) {
        zipCtrl.setValidators([Validators.minLength(9), Validators.required]);
        this.zipCodeValidLength = 9;
      }

      zipCtrl.updateValueAndValidity();
    }
  }
  cancelAddressChange() {
    this.store.dispatch(new ResetAddressFields());
  }
  submitAddressChangeRequest() {
    if (this.addressValid && this.isEmployeePortal) {
      const request: AddressChangeRequest = {
        StreetAddress: this.f.StreetAddress.value,
        StreetAddress2: this.f.StreetAddress2.value,
        City: this.f.City.value,
        State: this.f.State.value,
        PostalCode: this.f.PostalCode.value,
        Country: this.f.Country.value,
        IsNonUsAddress: this.f.IsNonUSAddress.value
      };
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
        this.changeRequestService
          .submitAddressChangeRequest(this.clientCode, p.Id, request)
          .subscribe(_ => {
            this.toastService.show('Change Request Submitted.');
            this.changeRequestService.getAddressChangeData(this.clientCode, p.Id).subscribe(val => {
              this.activeAddressChangeRequestData = val;
              this.hasActiveAddressChangeRequest = true;
              this.disableAddressControls();
              this.store.dispatch(new LoadPerson(this.clientCode, p.Id));
            });
          });
      });
    }
  }

  updatePersonLabel() {
    this.store.dispatch(new SetHeader(true));
  }

  validateMobilePhone() {
    const phone = this.f.MobilePhoneNumber.value;
    this.f.MobilePhoneNumber.markAsTouched();

    if (phone && phone.length === 10) {
      this.f.MobilePhoneNumber.clearValidators();
      this.f.MobilePhoneNumber.updateValueAndValidity();

      this.adminService
        .checkMobilePhone(phone)
        .pipe(
          catchError(err => {
            return of(false);
          })
        )
        .subscribe(valid => {
          if (!valid) {
            const formatted = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)} - ${phone.slice(6, 10)}`;
            this.ngModalService.dismissAll();
            this.modalService.showAlertModal(
              `Mobile number ${formatted} is not a valid mobile phone number. Please correct or remove to save changes.`,
              ModalStates.Error
            );

            // dumb trick to make this field invalid until we verify it
            this.f.MobilePhoneNumber.setValidators([Validators.minLength(11)]);
            this.f.MobilePhoneNumber.updateValueAndValidity();
          }
        });
    } else if (phone && phone.length < 10) {
      this.f.MobilePhoneNumber.setValidators([Validators.minLength(11)]);
    } else if (!phone) {
      this.f.MobilePhoneNumber.clearValidators();
    }

    this.f.MobilePhoneNumber.updateValueAndValidity();
  }

  public viewPendingRequest() {
    if (!this.hasActiveAddressChangeRequest) {
      return;
    }
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
      this.changeRequestService
        .getOpenAddressRequest(this.clientCode, p.Id, this.isEmployeePortal)
        .subscribe(r => {
          this.activeAddressChangeRequest = r;
        });
    });
  }
  public closeModal(
    result: {
      Operation: 'accept' | 'decline' | 'dismiss' | 'cancel';
      Result: boolean;
    } | null = null
  ) {
    this.activeAddressChangeRequest = null;
    if (!this.isEmployeePortal) {
      this.store.dispatch(new ClientSelectionLoadOpenChangeRequests());
    }
    if (result != null && result.Operation != 'dismiss') {
      this.hasActiveAddressChangeRequest = false;
      this.activeAddressChangeRequestData = null;
      this.sharedService.personEditsSaved.next();
      this.personalDeetsForm.markAsPristine();
      this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
        this.store.dispatch(new LoadPerson(this.clientCode, p.Id)).subscribe(() => {
          if (this.isEmployeePortal) {
            this.enableAddressControls();
          }
        });
      });
    }
  }

  // #region PRIVATE METHODS

  private _clearFederalId() {
    // bug 5339 clear MustMatch validator first
    // to prevent bug that crops up after multiple clears in one session
    this.personalDeetsForm.clearValidators();

    this.f.VerifyFederalIdentifier.setValue('', { emitEvent: false });
    this.f.FederalIdentifier.setValue('', { emitEvent: false });
    this.store.dispatch(new ClearSSN()).subscribe(() => {
      this.f.VerifyFederalIdentifier.setErrors(null);
      this.f.VerifyFederalIdentifier.clearValidators();
      this.f.VerifyFederalIdentifier.updateValueAndValidity();
      this.f.FederalIdentifier.markAsDirty(); // allow form to be saved
      this.store.dispatch(new SetMaskFederalId(false)); //show editable input
      this.store.dispatch(new SetPersonHasFederalId(false));
      this.onFederalIdChange(); // set boolean that determines if we post federal id
    });
  }

  private _loadEnums() {
    const obs = [this.enumService.get('EmpMaritalStatus'), this.enumService.get('EmployeeRace')];
    return forkJoin(obs).pipe(
      map(data => {
        this.maritalStatuses = data[0];
        this.races = data[1];
      })
    );
  }

  private _initForm() {
    this.store
      .selectOnce(PersonalDetailsFormSelectors.personalDetailsFormModel)
      .subscribe(model => {
        this.personalDeetsForm = this.formBuilder.group({
          EmploymentTypeId: new FormControl(model.EmploymentTypeId, Validators.required),
          UseEIN: new FormControl(model.UseEIN),
          FederalIdentifier: new FormControl(model.FederalIdentifier),
          VerifyFederalIdentifier: new FormControl(model.VerifyFederalIdentifier),
          IsCompany: new FormControl(model.IsCompany),
          FirstName: new FormControl(model.FirstName ?? '', Validators.required),
          MiddleName: new FormControl(model.MiddleName),
          LastName: new FormControl(model.LastName ?? '', Validators.required),
          PersonalEmailAddress: new FormControl(
            model.PersonalEmailAddress ?? '',
            [Validators.email],
            [this.emailBlacklistValidator.validate.bind(this.emailBlacklistValidator)]
          ),
          Birthday: new FormControl(model.Birthday),
          HomePhoneNumber: new FormControl(model.HomePhoneNumber),
          MobilePhoneNumber: new FormControl(model.MobilePhoneNumber),
          Gender: new FormControl(model.Gender),
          MaritalStatus: new FormControl(model.MaritalStatus),
          Race: new FormControl(model.Race),
          StreetAddress: new FormControl(model.StreetAddress),
          StreetAddress2: new FormControl(model.StreetAddress2),
          State: new FormControl(model.State),
          City: new FormControl(model.City),
          PostalCode: new FormControl(model.PostalCode),
          IsNonUSAddress: new FormControl(model.IsNonUSAddress),
          Country: new FormControl(model.Country)
        });
        this.personalDeetsForm.statusChanges
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe(status => {
            this.store.dispatch(new SetPersonalDetailsFormState(status));
            if (status != null) {
              if (status === 'INVALID') {
                this.store.selectOnce(PersonEditorState.getFederalIdChanged).subscribe(changed => {
                  //grab invalid control names
                  const invalidControls = Object.keys(this.personalDeetsForm.controls).filter(
                    key => this.personalDeetsForm.get(key).invalid
                  );
                  if (
                    !changed &&
                    invalidControls.every(
                      x => x === 'FederalIdentifier' || x === 'VerifyFederalIdentifier'
                    )
                  ) {
                    // if the form is invalid and the federal id has not been changed
                    //then the form is actually valid, provided Federal ID is the only valid control.
                    this.store.dispatch(new SetCurrentTabStatus('VALID'));
                  } else {
                    this.store.dispatch(new SetCurrentTabStatus(status));
                  }
                });
              } else {
                this.store.dispatch(new SetCurrentTabStatus(status));
              }
            }
          });
        this.store
          .select(PersonEditorState.triggerMarkFormPristine)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.personalDeetsForm.markAsPristine();
            this.personalDeetsForm.markAsUntouched();
            this.store.dispatch(new SetCurrentTabDirty(false));
          });
        this.personalDeetsForm.valueChanges
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe(() => {
            this.store.selectOnce(PersonEditorState.getCurrentTabStatus).subscribe(status => {
              if (this.personalDeetsForm.dirty && status.IsDirty != null && !status.IsDirty) {
                this.store.dispatch(new SetCurrentTabDirty(this.personalDeetsForm.dirty));
              }
            });
          });
        this.personalDeetsForm.get('FederalIdentifier').valueChanges.subscribe(() => {
          if (this.personalDeetsForm.get('FederalIdentifier').dirty) {
            this.onFederalIdChange().subscribe(() => {
              this.federalIdChange();
            });
          }
        });
        this.store
          .select(PersonEditorState.markFormTouched)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (this.initFinished) {
              const controls = this.personalDeetsForm.controls;
              Object.keys(controls).forEach(key => {
                controls[key].markAsTouched();
              });
            }
          });

        this._linkFormToState();
        this.personalDeetsForm.markAsUntouched();
        this.personalDeetsForm.markAsPristine();
      });
  }

  private _setAddressValidation(clear: boolean) {
    if (this.f.IsNonUSAddress.value) {
      this.f.StreetAddress.clearValidators();
      this.f.State.clearValidators();
      this.f.City.clearValidators();
      this.f.PostalCode.clearValidators();
    } else {
      this.f.StreetAddress.setValidators([Validators.required]);
      this.f.State.setValidators([Validators.required]);
      this.f.City.setValidators([Validators.required]);
      this.f.PostalCode.setValidators([Validators.minLength(5), Validators.required]);
    }

    if (clear) {
      this.personalDeetsForm.patchValue(
        {
          StreetAddress: '',
          StreetAddress2: '',
          State: '',
          City: '',
          PostalCode: '',
          Country: ''
        },
        { emitEvent: false, onlySelf: true }
      );
    }

    this.f.StreetAddress.updateValueAndValidity();
    this.f.State.updateValueAndValidity();
    this.f.City.updateValueAndValidity();
    this.f.PostalCode.updateValueAndValidity();
  }

  private _setFederalIdValidation(clearValues: boolean, ssnRequired: boolean = false) {
    if (this.f.UseEIN.value === '1') {
      this.f.FederalIdentifier.setValidators([
        Validators.minLength(9),
        this._sequentialSSN,
        this._ssnWithSameDigits
      ]);
    } else {
      this.f.FederalIdentifier.setValidators([
        Validators.minLength(9),
        this._sequentialSSN,
        this._ssnFirstDigitNine,
        this._ssnFirstSegmentSixes,
        this._ssnWithZeroedSegments,
        this._ssnWithSameDigits
      ]);
    }

    if (clearValues) {
      this._clearFederalId();
    }

    if (ssnRequired && this.f.UseEIN.value !== '1') {
      this.f.FederalIdentifier.addValidators([Validators.required]);
    }

    this.f.FederalIdentifier.updateValueAndValidity();
  }

  private _initChangeRequests() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(p => {
      this.changeRequestService.canEnableChangeRequests(this.clientCode).subscribe(canEnable => {
        this.isPersonActive.pipe(take(1)).subscribe(isActive => {
          this.canSubmitRequests = canEnable.Value && isActive;
          if (canEnable.Value) {
            this.changeRequestService
              .hasAddressChangeRequest(this.clientCode, p.Id, this.isEmployeePortal)
              .subscribe(result => {
                this.hasActiveAddressChangeRequest = result.Value;
                if (result.Value) {
                  this.changeRequestService
                    .getAddressChangeData(this.clientCode, p.Id, this.isEmployeePortal)
                    .subscribe(val => {
                      this.activeAddressChangeRequestData = val;
                    });
                }
              });
          } else if (this.isEmployeePortal) {
            this.disableAddressControls();
          }
        });
      });
    });
  }

  private _setNameValidation() {
    // if using company name, drop first name requirement.
    /// pass company name as last name
    if (this.f.IsCompany.value) {
      this.f.FirstName.clearValidators();
    } else {
      this.f.FirstName.setValidators([Validators.required]);
    }

    this.f.FirstName.updateValueAndValidity();
  }

  _showActiveEarningWarning(typeId) {
    let typeText = '';

    if (typeId == EmploymentTypes.W2) {
      typeText = 'W-2';
    } else if (typeId == EmploymentTypes.M1099) {
      typeText = '1099-M';
    } else if (typeId == EmploymentTypes.K1) {
      typeText = 'K-1';
    }

    this.modalService.showAlertModal(
      `This person has been added with an employment type of ${typeText}. The client does not have an active ${typeText} earning.`,
      ModalStates.Warning
    );
  }

  // SSN validator function - disallow sequential numbers
  private _sequentialSSN(control: AbstractControl): {
    [key: string]: any;
  } | null {
    return control.value == '123456789' ||
      control.value == '987654321' ||
      control.value == '012345678' ||
      control.value == '876543210'
      ? { sequentialNotAllowed: { value: 'Invalid SSN' } }
      : null;
  }

  // SSN validator function - don't accept SSNs beginning with 9
  private _ssnFirstDigitNine(control: AbstractControl): {
    [key: string]: any;
  } | null {
    if (
      /^9/.test(control.value) &&
      control.parent.controls['EmploymentTypeId']?.value != EmploymentTypes.M1099
    ) {
      return { startWithNine: { message: 'cannot start with 9' } };
    }

    return null;
  }

  // SSN validator function - reject SSNs with first segment of '666'
  private _ssnFirstSegmentSixes(control: AbstractControl): {
    [key: string]: any;
  } | null {
    if (/^666\d{6}/.test(control.value)) {
      return {
        firstSegmentSixes: { message: 'first segment cannot be 666' }
      };
    }

    return null;
  }

  // SSN validator function - reject SSNs that are just one number repeated 9 times
  private _ssnWithSameDigits(control: AbstractControl): {
    [key: string]: any;
  } | null {
    return /(1{9})|(2{9})|(3{9})|(4{9})|(5{9})|(6{9})|(7{9})|(8{9})|(9{9})/.test(control.value)
      ? { sameDigits: { message: 'cannot contain all the same digits' } }
      : null;
  }

  // SSN validator function - reject SSNs with zeroed-out segments
  private _ssnWithZeroedSegments(control: AbstractControl): {
    [key: string]: any;
  } | null {
    const message = 'first segment cannot be all zeroes';
    if (/^000\d{6}/.test(control.value)) {
      return {
        zeroedSegment: { message: message }
      };
    } else if (/^\d{3}00\d{4}/.test(control.value)) {
      return {
        zeroedSegment: { message: message }
      };
    } else if (/\d{5}0000$/.test(control.value)) {
      return {
        zeroedSegment: { message: message }
      };
    }

    return null;
  }

  private _linkFormToState() {
    this.personalDeetsForm
      .get('EmploymentTypeId')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetEmploymentTypeId(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.employmentTypeId)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('EmploymentTypeId');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('UseEIN')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetUseEIN(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.useEIN)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('UseEIN');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('FederalIdentifier')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFederalIdentifier(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.federalIdentifier)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('FederalIdentifier');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('VerifyFederalIdentifier')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetVerifyFederalIdentifier(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.verifyFederalIdentifier)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('VerifyFederalIdentifier');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('IsCompany')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetIsCompany(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.isCompany)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('IsCompany');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('FirstName')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFirstName(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.firstName)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('FirstName');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('MiddleName')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetMiddleName(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.middleName)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('MiddleName');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('LastName')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetLastName(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.lastName)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('LastName');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('Birthday')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetBirthday(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.birthday)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('Birthday');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('PersonalEmailAddress')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetPersonalEmailAddress(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.personalEmailAddress)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('PersonalEmailAddress');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('HomePhoneNumber')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHomePhoneNumber(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.homePhoneNumber)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('HomePhoneNumber');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('MobilePhoneNumber')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetMobilePhoneNumber(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.mobilePhoneNumber)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('MobilePhoneNumber');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('Gender')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetGender(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.gender)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('Gender');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('MaritalStatus')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetMaritalStatus(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.maritalStatus)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('MaritalStatus');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('Race')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetRace(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.race)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('Race');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('StreetAddress')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStreetAddress(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.streetAddress)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('StreetAddress');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('StreetAddress2')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStreetAddress2(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.streetAddress2)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('StreetAddress2');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('City')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetCity(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.city)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('City');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('State')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetState(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.state)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('State');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('PostalCode')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetPostalCode(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.postalCode)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('PostalCode');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('Country')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetCountry(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.country)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('Country');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.personalDeetsForm
      .get('IsNonUSAddress')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetIsNonUSAddress(val));
      });
    this.store
      .select(PersonalDetailsFormSelectors.isNonUSAddress)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.personalDeetsForm.get('IsNonUSAddress');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });

    this.store.dispatch(new SetCurrentTabDirty(false));
    this.store.dispatch(new SetCurrentTabStatus(this.personalDeetsForm.status));
  }

  // #endregion
}
